/**
 * English Privacy Policy translations
 */
export const privacy = {
  pageTitle: "Privacy Policy",
  heroSubtitle: "Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.",
  lastUpdated: "Last Updated: January 2026",
  effectiveDate: "Effective: January 1, 2026",

  intro: {
    title: "Introduction",
    welcome: "Welcome to the JCI Development Councils Youth Futures Observatory. We are committed to protecting your privacy and ensuring transparency about how we handle your data.",
    commitment: "This Privacy Policy explains what personal information we collect, how we use it, and your rights regarding your data. We follow the principles of data minimization and purpose limitation.",
    scope: "This policy applies to all users of the Youth Futures Observatory platform, including members of JCI (Junior Chamber International) and public visitors.",
  },

  controller: {
    title: "Data Controller",
    description: "The data controller responsible for your personal information is:",
    name: "JCI Development Councils",
    contact: "For privacy-related inquiries, please contact:",
  },

  collection: {
    title: "Information We Collect",
    description: "We collect different types of information depending on how you interact with our platform:",
    account: {
      title: "Account Information",
      email: "Email address (for authentication and notifications)",
      name: "Display name (optional, for personalization)",
      avatar: "Profile picture (optional, stored securely)",
      organization: "JCI organization affiliation (optional)",
    },
    preferences: {
      title: "User Preferences",
      language: "Preferred language setting",
      theme: "Light/dark mode preference",
      country: "Focus country for data analysis",
      interest: "Primary area of interest (e.g., Mental Health, Entrepreneurship)",
    },
    analytics: {
      title: "Analytics Data (with consent)",
      description: "When you consent to analytics, we collect:",
      pages: "Pages visited and navigation patterns",
      scroll: "Scroll depth on content pages",
      time: "Time spent on the platform",
      device: "Browser and device type (anonymized)",
    },
  },

  usage: {
    title: "How We Use Your Data",
    description: "We use your information for the following purposes:",
    purpose1: {
      title: "Providing Services",
      description: "To authenticate you, personalize your experience, and deliver the Observatory features you request.",
    },
    purpose2: {
      title: "Platform Improvement",
      description: "To understand how users interact with our platform and improve functionality (only with analytics consent).",
    },
    purpose3: {
      title: "Communications",
      description: "To send you important updates about the platform, if you've opted in to notifications.",
    },
    purpose4: {
      title: "Security",
      description: "To protect against unauthorized access and maintain the integrity of our systems.",
    },
  },

  legal: {
    title: "Legal Basis for Processing",
    description: "Under GDPR, we process your data based on the following legal grounds:",
    consent: {
      title: "Consent (Article 6(1)(a))",
      description: "For analytics tracking and marketing communications. You can withdraw consent at any time.",
    },
    contract: {
      title: "Contract Performance (Article 6(1)(b))",
      description: "To provide the services you've requested when you create an account.",
    },
    legitimate: {
      title: "Legitimate Interests (Article 6(1)(f))",
      description: "For security measures and fraud prevention, balanced against your rights.",
    },
    obligation: {
      title: "Legal Obligation (Article 6(1)(c))",
      description: "To comply with applicable laws and regulations.",
    },
  },

  cookies: {
    title: "Cookies & Local Storage",
    description: "We use cookies and browser storage to enhance your experience:",
    essential: {
      title: "Essential Cookies",
      description: "Required for authentication and basic functionality. Cannot be disabled.",
    },
    analytics: {
      title: "Analytics Cookies (Google Analytics 4)",
      description: "Help us understand how visitors use our site. Only active with your consent.",
    },
    preferences: {
      title: "Preference Storage (localStorage)",
      description: "Stores your language, theme, and consent choices locally in your browser.",
    },
    manage: "You can manage your cookie preferences at any time in",
    manageLink: "Settings",
  },

  thirdParty: {
    title: "Third-Party Services",
    description: "We use the following third-party services to operate our platform:",
    ga4: "Website analytics and usage tracking (with consent)",
    lovable: "Backend infrastructure, authentication, and database",
    osm: "Interactive maps displaying JCI global presence",
    fonts: "Typography delivery (Plus Jakarta Sans font family)",
  },

  retention: {
    title: "Data Retention",
    description: "We retain your data only as long as necessary for the purposes described:",
    dataType: "Data Type",
    period: "Retention Period",
    account: "Account Data",
    accountPeriod: "Until account deletion or 2 years of inactivity",
    analytics: "Analytics Data",
    analyticsPeriod: "14 months (Google Analytics default)",
    logs: "Error Logs",
    logsPeriod: "90 days",
  },

  rights: {
    title: "Your Privacy Rights (GDPR)",
    description: "Under the General Data Protection Regulation, you have the following rights:",
    access: {
      title: "Right of Access",
      description: "Request a copy of all personal data we hold about you.",
    },
    rectification: {
      title: "Right to Rectification",
      description: "Request correction of inaccurate or incomplete personal data.",
    },
    erasure: {
      title: "Right to Erasure",
      description: "Request deletion of your personal data ('right to be forgotten').",
    },
    restriction: {
      title: "Right to Restriction",
      description: "Request limitation of processing in certain circumstances.",
    },
    portability: {
      title: "Right to Data Portability",
      description: "Receive your data in a structured, machine-readable format.",
    },
    object: {
      title: "Right to Object",
      description: "Object to processing based on legitimate interests or for direct marketing.",
    },
    exercise: "To exercise these rights, visit your",
    settingsLink: "Settings page",
  },

  international: {
    title: "International Privacy Compliance",
    description: "As a global organization operating in over 100 countries, we are committed to respecting local privacy laws and regulations wherever our members are located.",
    commitment: "While we use GDPR as our baseline standard (as it provides the most comprehensive protections), we also comply with applicable local privacy laws including:",
    ukGdpr: "UK GDPR for United Kingdom residents",
    lgpd: "LGPD (Lei Geral de Proteção de Dados) for Brazilian residents",
    pipeda: "PIPEDA for Canadian residents",
    appi: "APPI (Act on Protection of Personal Information) for Japanese residents",
    localRights: "If your country has specific privacy legislation, you may have additional rights. Please contact us to understand how your local laws apply.",
  },

  children: {
    title: "Children's Privacy",
    description: "The Youth Futures Observatory is designed for users aged 18 and above, in line with JCI membership requirements. We do not knowingly collect data from children under 16.",
    action: "If you believe we have inadvertently collected data from a minor, please contact us immediately at privacy@jci.cc.",
  },

  security: {
    title: "Data Security",
    description: "We implement robust security measures to protect your data:",
    encryption: "All data is encrypted in transit (HTTPS/TLS) and at rest",
    access: "Strict access controls limit who can view your information",
    rls: "Row-Level Security (RLS) ensures you can only access your own data",
    audit: "Regular security audits and vulnerability assessments",
  },

  transfers: {
    title: "International Data Transfers",
    description: "Your data may be processed in countries outside your residence, including the United States and European Union.",
    safeguards: "We ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) where required by law.",
  },

  changes: {
    title: "Changes to This Policy",
    description: "We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements.",
    notification: "Significant changes will be communicated via email (if you have an account) or through a prominent notice on our platform. The 'Last Updated' date at the top indicates when changes were made.",
  },

  contact: {
    title: "Contact Us",
    description: "If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:",
    email: "Privacy Inquiries:",
    dpa: "You also have the right to lodge a complaint with your local data protection authority if you believe your rights have been violated.",
  },
} as const;
